import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Search,
  Star,
  MapPin,
  MessageCircle,
  ArrowLeft,
  Filter,
  Dumbbell,
  Send,
  X,
  Building2,
  User,
  Award,
} from "lucide-react";
import { API } from "@/App";
import ReviewsSection from "@/components/ReviewsSection";
import { useLocalizedData } from "@/contexts/LocalizedDataContext";
import { useTranslation } from "react-i18next";

export default function TrainersListPage() {
  const navigate = useNavigate();
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard", "employees"]);
  const { getLocalizedBranch, getLocalizedTrainingType } = useLocalizedData();

  const [trainers, setTrainers] = useState([]);
  const [businesses, setBusinesses] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [selectedBranch, setSelectedBranch] = useState("");
  const [trainerStatusFilter, setTrainerStatusFilter] = useState("all"); // all, has_business, working, looking, not_working
  const [trainerCities, setTrainerCities] = useState([]); // Sadece antrenörlerin şehirleri
  const [branches, setBranches] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  
  // Message modal
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [selectedTrainer, setSelectedTrainer] = useState(null);
  const [message, setMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  
  // Trainer detail modal
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailTrainer, setDetailTrainer] = useState(null);

  useEffect(() => {
    fetchData();
    checkUser();
  }, []);

  const checkUser = async () => {
    try {
      const response = await fetch(`${API}/auth/me`, { credentials: "include" });
      if (response.ok) {
        setCurrentUser(await response.json());
      }
    } catch (error) {
      console.error("Error checking user:", error);
    }
  };

  const fetchData = async () => {
    try {
      const [trainersRes, branchesRes] = await Promise.all([
        fetch(`${API}/public/trainers`),
        fetch(`${API}/branches`),
      ]);

      if (trainersRes.ok) {
        const trainersData = await trainersRes.json();
        setTrainers(trainersData);
        
        // Fetch business info for each trainer
        const businessIds = [...new Set(trainersData.map(t => t.business_id).filter(Boolean))];
        const businessPromises = businessIds.map(id => 
          fetch(`${API}/businesses/${id}`).then(r => r.ok ? r.json() : null)
        );
        const businessResults = await Promise.all(businessPromises);
        
        const businessMap = {};
        businessResults.forEach(b => {
          if (b) businessMap[b.business_id] = b;
        });
        setBusinesses(businessMap);
        
        // Sadece antrenörlerin çalıştığı işletmelerin şehirlerini al
        const citiesFromTrainers = new Set();
        trainersData.forEach(t => {
          const business = businessMap[t.business_id];
          if (business?.city) {
            citiesFromTrainers.add(business.city);
          }
        });
        setTrainerCities([...citiesFromTrainers].sort());
      }
      
      if (branchesRes.ok) {
        setBranches(await branchesRes.json());
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Deneyim hesaplama (mesleğe başlama tarihinden)
  const calculateExperience = (trainer) => {
    if (!trainer.career_start_date) return null;
    
    const startDate = new Date(trainer.career_start_date);
    const today = new Date();
    const diffTime = today - startDate;
    const diffYears = Math.floor(diffTime / (1000 * 60 * 60 * 24 * 365.25));
    
    return diffYears;
  };

  const filteredTrainers = trainers.filter((t) => {
    const business = businesses[t.business_id];
    
    if (searchQuery) {
      const fullName = `${t.name || ""} ${t.surname || ""}`.toLowerCase();
      if (!fullName.includes(searchQuery.toLowerCase())) {
        return false;
      }
    }
    
    if (selectedCity && selectedCity !== "all") {
      if (!business || business.city !== selectedCity) {
        return false;
      }
    }
    
    if (selectedBranch && selectedBranch !== "all") {
      if (!t.branches?.includes(selectedBranch)) {
        return false;
      }
    }
    
    // Gelişmiş antrenör durumu filtresi
    if (trainerStatusFilter === "has_business") {
      // İşletmesi olan antrenörler (kendi işletmesi var)
      if (!t.has_own_business) return false;
    } else if (trainerStatusFilter === "working") {
      // Bir işletmede çalışan antrenörler
      if (!t.business_id) return false;
    } else if (trainerStatusFilter === "looking") {
      // İş arayan antrenörler
      if (!t.looking_for_work) return false;
    } else if (trainerStatusFilter === "not_working") {
      // Çalışmayan antrenörler (hiçbir işletmede çalışmıyor)
      if (t.business_id) return false;
    }
    
    return true;
  });

  // Aynı user_id'ye sahip antrenörleri grupla (birden fazla işletmede çalışan)
  const groupedTrainers = filteredTrainers.reduce((acc, trainer) => {
    const key = trainer.user_id || trainer.trainer_id;
    if (!acc[key]) {
      // İşletmesi varsa ekle
      const businessInfo = trainer.business_id && businesses[trainer.business_id] ? {
        business_id: trainer.business_id, 
        business_name: businesses[trainer.business_id]?.name || t("common:business"),
        city: businesses[trainer.business_id]?.city,
        district: businesses[trainer.business_id]?.district
      } : null;
      
      acc[key] = {
        ...trainer,
        businessList: businessInfo ? [businessInfo] : [],
        allBranches: [...(trainer.branches || [])]
      };
    } else {
      // Aynı antrenör farklı işletmede - işletmeyi listeye ekle
      if (trainer.business_id && businesses[trainer.business_id]) {
        const existingBusiness = acc[key].businessList.find(b => b.business_id === trainer.business_id);
        if (!existingBusiness) {
          acc[key].businessList.push({
            business_id: trainer.business_id,
            business_name: businesses[trainer.business_id]?.name || t("common:business"),
            city: businesses[trainer.business_id]?.city,
            district: businesses[trainer.business_id]?.district
          });
        }
      }
      // Branşları birleştir
      (trainer.branches || []).forEach(b => {
        if (!acc[key].allBranches.includes(b)) {
          acc[key].allBranches.push(b);
        }
      });
    }
    return acc;
  }, {});

  const uniqueTrainers = Object.values(groupedTrainers);

  const handleSendMessage = async () => {
    if (!currentUser) {
      toast.error(t('messaging:loginToSendMessage'));
      return;
    }
    
    if (!message.trim()) {
      toast.error(t("common:toast_mesajBosOlamaz"));
      return;
    }
    
    setSendingMessage(true);
    try {
      const response = await fetch(`${API}/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          receiver_id: selectedTrainer.user_id,
          content: message,
        }),
      });
      
      if (response.ok) {
        toast.success(t("common:toast_mesajGonderildi"));
        setMessage("");
        setShowMessageModal(false);
      } else {
        toast.error(t('messaging:messageNotSent'));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setSendingMessage(false);
    }
  };

  const openMessageModal = (trainer) => {
    if (!currentUser) {
      toast.error(t('messaging:loginToSendMessage'));
      navigate("/auth");
      return;
    }
    setSelectedTrainer(trainer);
    setShowMessageModal(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-navy text-white py-8">
        <div className="container mx-auto px-4">
          <Button
            variant="ghost"
            className="text-white hover:text-white/80 mb-4"
            onClick={() => navigate("/")}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t("common:homePage")}
          </Button>
          
          <h1 className="text-3xl font-heading font-bold">{t('common:trainers')}</h1>
          <p className="text-white/70 mt-2">
            {t("trainers:discoverTrainers")}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-grey" />
              <Input
                placeholder={t('trainers:searchTrainer')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 rounded-xl"
                data-testid="trainer-search"
              />
            </div>
            
            <Select value={selectedCity} onValueChange={setSelectedCity}>
              <SelectTrigger className="w-[140px] rounded-xl" data-testid="city-filter">
                <SelectValue placeholder={t('common:city')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("common:tumSehirler")}</SelectItem>
                {trainerCities.map((city) => (
                  <SelectItem key={city} value={city}>
                    {city}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={selectedBranch} onValueChange={setSelectedBranch}>
              <SelectTrigger className="w-[140px] rounded-xl" data-testid="branch-filter">
                <SelectValue placeholder={t('common:branch')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("common:tumBranslar")}</SelectItem>
                {branches.map((branch) => (
                  <SelectItem key={branch.id} value={branch.name}>
                    {getLocalizedBranch(branch.name)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={trainerStatusFilter} onValueChange={setTrainerStatusFilter}>
              <SelectTrigger className="w-[180px] rounded-xl" data-testid="work-filter">
                <SelectValue placeholder={t('common:status')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('trainers:allTrainers')}</SelectItem>
                <SelectItem value="has_business">{t("common:i̇sletmesiOlanlar")}</SelectItem>
                <SelectItem value="working">{t('employees:employees')}</SelectItem>
                <SelectItem value="looking">{t("common:i̇sArayanlar")}</SelectItem>
                <SelectItem value="not_working">{t("common:calismayanlar")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Trainer List */}
      <div className="container mx-auto px-4 py-8">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy"></div>
          </div>
        ) : uniqueTrainers.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {uniqueTrainers.map((trainer) => {
              return (
                <Card 
                  key={trainer.user_id || trainer.trainer_id} 
                  className="border-0 shadow-sm hover:shadow-lg transition-all cursor-pointer"
                  data-testid={`trainer-card-${trainer.user_id || trainer.trainer_id}`}
                  onClick={() => {
                    setDetailTrainer(trainer);
                    setShowDetailModal(true);
                  }}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <Avatar className="h-16 w-16">
                        <AvatarFallback className="bg-navy text-white text-xl">
                          {trainer.name?.charAt(0) || "A"}
                        </AvatarFallback>
                      </Avatar>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-heading font-bold text-navy text-lg truncate">
                            {trainer.name} {trainer.surname}
                          </h3>
                          {/* Role badges */}
                          <Badge variant="outline" className="text-xs border-navy text-navy">
                            {t("common:trainer")}
                          </Badge>
                          {/* Deneyim badge - sadece mesleğe başlama tarihi varsa göster */}
                          {(() => {
                            const exp = calculateExperience(trainer);
                            if (exp !== null && exp > 0) {
                              return (
                                <Badge variant="outline" className="text-xs border-amber-500 text-amber-600">
                                  {exp} {t("trainers:yearsExperience")}
                                </Badge>
                              );
                            }
                            return null;
                          })()}
                          {/* Yeni Antrenör badge - son 30 günde katılanlar */}
                          {(() => {
                            const createdAt = trainer.created_at ? new Date(trainer.created_at) : null;
                            if (createdAt) {
                              const daysSinceCreation = Math.floor((new Date() - createdAt) / (1000 * 60 * 60 * 24));
                              if (daysSinceCreation <= 30) {
                                return (
                                  <Badge className="text-xs bg-gradient-to-r from-pink-500 to-purple-500 text-white animate-pulse">
                                    ✨ {t("trainers:newest")}
                                  </Badge>
                                );
                              }
                            }
                            return null;
                          })()}
                          {trainer.has_own_business && (
                            <Badge className="text-xs bg-emerald-500 text-white">
                              {t("trainers:businessOwner")}
                            </Badge>
                          )}
                          {trainer.looking_for_work && (
                            <Badge className="bg-green-500 text-white text-xs animate-pulse">
                              {t("trainers:lookingForWork")}
                            </Badge>
                          )}
                        </div>
                        
                        {/* Branches - tüm branşları göster */}
                        <div className="flex flex-wrap gap-1 mt-2">
                          {(trainer.allBranches || trainer.branches)?.slice(0, 3).map((branch) => (
                            <Badge 
                              key={branch} 
                              variant="secondary"
                              className="text-xs bg-navy/10 text-navy"
                            >
                              {getLocalizedBranch(branch)}
                            </Badge>
                          ))}
                          {(trainer.allBranches || trainer.branches)?.length > 3 && (
                            <Badge variant="secondary" className="text-xs">
                              +{(trainer.allBranches || trainer.branches).length - 3}
                            </Badge>
                          )}
                        </div>
                        
                        {/* Rating */}
                        <div className="flex items-center gap-1 mt-2">
                          <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                          <span className="font-semibold text-sm">
                            {trainer.rating?.toFixed(1) || "5.0"}
                          </span>
                          <span className="text-grey text-sm">
                            ({trainer.rating_count || 0} {t("trainers:evaluations")})
                          </span>
                        </div>
                        
                        {/* Çalıştığı İşletmeler */}
                        {trainer.businessList && trainer.businessList.length > 0 ? (
                          <div className="mt-2 space-y-1">
                            {trainer.businessList.map((biz, idx) => (
                              <div key={biz.business_id} className="flex items-center gap-2 text-sm text-grey">
                                <Building2 className="w-4 h-4 flex-shrink-0" />
                                <Link 
                                  to={`/business/${biz.business_id}`}
                                  className="hover:text-navy hover:underline truncate"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {biz.business_name}
                                </Link>
                                {idx === 0 && trainer.businessList.length > 1 && (
                                  <span className="text-xs text-navy font-medium">+{trainer.businessList.length - 1} işletme</span>
                                )}
                              </div>
                            )).slice(0, 1)}
                          </div>
                        ) : !trainer.has_own_business ? (
                          /* Hiçbir işletmede çalışmıyor VE kendi işletmesi de yok */
                          <div className="mt-2">
                            <div className="flex items-center gap-2 text-sm text-slate-500">
                              <Building2 className="w-4 h-4 flex-shrink-0" />
                              <span>{t("common:bagimsizAntrenor")}</span>
                            </div>
                          </div>
                        ) : null}
                        
                        {/* Own Business */}
                        {trainer.has_own_business && trainer.own_business_name && (
                          <div className="flex items-center gap-2 mt-1 text-sm text-emerald-600">
                            <Building2 className="w-4 h-4" />
                            <span className="truncate">{trainer.own_business_name}</span>
                            <span className="text-xs">{t("trainers:ownBusiness")}</span>
                          </div>
                        )}
                        
                        {/* Location - ilk işletmenin konumu */}
                        {trainer.businessList?.[0]?.city && (
                          <div className="flex items-center gap-2 mt-1 text-sm text-grey">
                            <MapPin className="w-4 h-4" />
                            <span className="truncate">
                              {trainer.businessList[0].district}, {trainer.businessList[0].city}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Action Button */}
                    <Button
                      className="w-full mt-4 bg-navy hover:bg-navy/90 rounded-xl"
                      onClick={(e) => {
                        e.stopPropagation();
                        openMessageModal(trainer);
                      }}
                      data-testid={`message-btn-${trainer.trainer_id}`}
                    >
                      <MessageCircle className="w-4 h-4 mr-2" />
                      {t("trainers:sendMessage")}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12">
            <Dumbbell className="w-16 h-16 text-grey mx-auto mb-4" />
            <h3 className="text-xl font-heading font-bold text-navy mb-2">
              {t("trainers:noTrainersFound")}
            </h3>
            <p className="text-grey">
              Arama kriterlerinize uygun antrenör bulunamadı
            </p>
          </div>
        )}
      </div>

      {/* Trainer Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-4">
              <Avatar className="h-16 w-16">
                <AvatarFallback className="bg-navy text-white text-xl">
                  {detailTrainer?.name?.charAt(0) || "A"}
                </AvatarFallback>
              </Avatar>
              <div>
                <h2 className="text-xl font-heading font-bold text-navy">
                  {detailTrainer?.name} {detailTrainer?.surname}
                </h2>
                {detailTrainer?.looking_for_work && (
                  <Badge className="bg-green-500 text-white text-xs mt-1">
                    {t("trainers:lookingForWork")}
                  </Badge>
                )}
              </div>
            </DialogTitle>
          </DialogHeader>
          
          {detailTrainer && (
            <div className="space-y-6 mt-4">
              {/* Info Cards */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-slate-50 rounded-xl">
                  <div className="flex items-center gap-2 text-grey mb-1">
                    <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    <span className="text-sm">{t('business:rating')}</span>
                  </div>
                  <p className="text-xl font-bold text-navy">
                    {detailTrainer.rating?.toFixed(1) || "5.0"}
                    <span className="text-sm font-normal text-grey ml-1">
                      ({detailTrainer.rating_count || 0})
                    </span>
                  </p>
                </div>
                
                <div className="p-4 bg-slate-50 rounded-xl">
                  <div className="flex items-center gap-2 text-grey mb-1">
                    <Award className="w-4 h-4" />
                    <span className="text-sm">{t("trainers:experience")}</span>
                  </div>
                  <p className="text-xl font-bold text-navy">
                    {(() => {
                      const exp = calculateExperience(detailTrainer);
                      if (exp === null) return "-";
                      return `${exp} yıl`;
                    })()}
                  </p>
                </div>
              </div>
              
              {/* Branches - tüm branşları göster */}
              {(detailTrainer.allBranches || detailTrainer.branches)?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-navy mb-2 flex items-center gap-2">
                    <Dumbbell className="w-4 h-4" />
                    Uzmanlık Alanları
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {(detailTrainer.allBranches || detailTrainer.branches).map((branch) => (
                      <Badge key={branch} variant="secondary" className="bg-navy/10 text-navy">
                        {getLocalizedBranch(branch)}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Bio */}
              {detailTrainer.bio && (
                <div>
                  <h4 className="font-semibold text-navy mb-2 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Hakkında
                  </h4>
                  <p className="text-grey">{detailTrainer.bio}</p>
                </div>
              )}
              
              {/* Tüm Çalıştığı İşletmeler */}
              {detailTrainer.businessList && detailTrainer.businessList.length > 0 ? (
                <div>
                  <h4 className="font-semibold text-navy mb-2 flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    Çalıştığı İşletme{detailTrainer.businessList.length > 1 ? "ler" : ""}
                  </h4>
                  <div className="space-y-2">
                    {detailTrainer.businessList.map((biz) => (
                      <div key={biz.business_id} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                        <Link 
                          to={`/business/${biz.business_id}`}
                          className="text-navy hover:underline font-medium"
                          onClick={() => setShowDetailModal(false)}
                        >
                          {biz.business_name}
                        </Link>
                        {biz.city && (
                          <span className="text-sm text-grey">
                            {biz.district}, {biz.city}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div>
                  <h4 className="font-semibold text-navy mb-2 flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    Çalıştığı İşletme
                  </h4>
                  <p className="text-slate-500 text-sm">{t("common:suAnHerhangiBirIsletmede")}</p>
                </div>
              )}
              
              {/* Reviews Section */}
              <div className="border-t pt-6">
                <ReviewsSection
                  targetType="trainer"
                  targetId={detailTrainer.trainer_id}
                  targetName={`${detailTrainer.name} ${detailTrainer.surname}`}
                  currentUserId={currentUser?.user_id}
                  showCreateButton={true}
                />
              </div>
              
              {/* Action Button */}
              <Button
                className="w-full bg-navy hover:bg-navy/90 rounded-xl"
                onClick={() => {
                  setShowDetailModal(false);
                  openMessageModal(detailTrainer);
                }}
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                {t("trainers:sendMessage")}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Message Modal */}
      <Dialog open={showMessageModal} onOpenChange={setShowMessageModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <Avatar className="h-10 w-10">
                <AvatarFallback className="bg-navy text-white">
                  {selectedTrainer?.name?.charAt(0) || "A"}
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="font-heading font-bold">
                  {selectedTrainer?.name} {selectedTrainer?.surname}
                </p>
                <p className="text-sm text-grey font-normal">
                  Mesaj gönder
                </p>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <Textarea
              placeholder={t('messaging:typeMessage')}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              className="rounded-xl"
              data-testid="message-textarea"
            />
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1 rounded-xl"
                onClick={() => setShowMessageModal(false)}
              >
                İptal
              </Button>
              <Button
                className="flex-1 bg-navy hover:bg-navy/90 rounded-xl"
                onClick={handleSendMessage}
                disabled={sendingMessage || !message.trim()}
                data-testid="send-message-btn"
              >
                {sendingMessage ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Gönder
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
